import { memo, useEffect, useMemo, useState } from 'react'
import { announcementsApi, type ImageFileEntry } from '../../lib/infoserviceApi.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

interface PickerProps {
  channelId: string
  currentPath: string | null
  onPick: (path: string) => void
  onClose: () => void
}

/**
 * « Choisir dans INFOSERVICES » : images PNG/JPEG déjà présentes sur le disque
 * de diffusion. Le fichier est rattaché tel quel, sans copie ni renommage.
 */
export const AnnouncementImagePicker = memo(function AnnouncementImagePicker({ channelId, currentPath, onPick, onClose }: PickerProps) {
  const [files, setFiles] = useState<ImageFileEntry[] | null>(null)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(currentPath)

  useEffect(() => {
    let cancelled = false
    announcementsApi.imageFiles(channelId)
      .then(list => { if (!cancelled) setFiles(list) })
      .catch(cause => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Lecture du dossier impossible') })
    return () => { cancelled = true }
  }, [channelId])

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('fr')
    return (files ?? []).filter(file => !needle || file.path.toLocaleLowerCase('fr').includes(needle))
  }, [files, search])

  return (
    <ModalShell title="Choisir une image dans INFOSERVICES" size="wide" onClose={onClose} className="announcement-picker">
      <div className="tp-modal__body announcement-picker__body">
        <input
          className="tp-input"
          type="search"
          placeholder="Nom de fichier…"
          aria-label="Filtrer les images"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
        {!files && !error && <div className="tp-empty-state"><Spinner /> Lecture du dossier…</div>}
        {files && visible.length === 0 && (
          <div className="tp-empty-state">
            {files.length === 0 ? 'Aucune image PNG ou JPEG dans INFOSERVICES.' : 'Aucune image ne correspond.'}
          </div>
        )}
        <ul className="announcement-picker__grid">
          {visible.map(file => (
            <li key={file.path}>
              <button
                type="button"
                className={`announcement-picker__item${selected === file.path ? ' is-selected' : ''}`}
                aria-pressed={selected === file.path}
                onClick={() => setSelected(file.path)}
                onDoubleClick={() => onPick(file.path)}
              >
                <img src={announcementsApi.imageFileUrl(channelId, file.path, { size: 'card', version: file.modifiedAt })} alt="" width={480} height={270} loading="lazy" decoding="async" />
                <span className="announcement-picker__name">{file.path}</span>
                {file.usedBy.length > 0 && (
                  <span className="announcement-picker__used">Utilisée par {file.usedBy.map(entry => entry.label).join(', ')}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <footer className="tp-modal__footer">
        <button type="button" className="tp-button" onClick={onClose}>Annuler</button>
        <button type="button" className="tp-button tp-button--primary" disabled={!selected || selected === currentPath} onClick={() => selected && onPick(selected)}>
          Rattacher cette image
        </button>
      </footer>
    </ModalShell>
  )
})
